import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlateCombobox } from "@/components/PlateCombobox";
import { api, errMsg } from "@/lib/api";

const F = ({ label, children }) => <div className="min-w-0 space-y-1.5"><Label>{label}</Label>{children}</div>;

export const FormDialog = ({ sid, isAdmin, onClose, onSaved }) => {
  const [templates, setTemplates] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [f, setF] = useState({ template_id: "", driver_id: "", birth_place: "", birth_date: "", place: "" });
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/forms/templates").then((r) => { setTemplates(r.data); if (r.data[0]) setF((p) => ({ ...p, template_id: p.template_id || r.data[0].id })); });
    api.get("/drivers", { params: { station_id: sid } }).then((r) => setDrivers(r.data.filter((d) => d.active)));
  }, [sid]);
  const t = templates.find((x) => x.id === f.template_id);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const save = async (signNow) => {
    setBusy(true);
    try {
      const { data } = await api.post("/forms", { ...f, birth_date: f.birth_date || null, station_id: isAdmin ? sid : undefined });
      for (const [slot, file] of Object.entries(files)) {
        const fd = new FormData();
        fd.append("slot", slot);
        fd.append("file", file);
        try { await api.post(`/forms/${data.id}/photos`, fd); } catch (e) { toast.error(`${t.photos[slot]}: ${errMsg(e)}`); }
      }
      toast.success("Modulo creato");
      onSaved(signNow ? data : null);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="form-dialog">
        <DialogHeader>
          <DialogTitle>Nuovo modulo</DialogTitle>
          <DialogDescription>Scegli il modello e il driver, fotografa il documento. Poi il driver firma subito oppure dal link che gli mandi.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Modello">
            <Select value={f.template_id || undefined} onValueChange={set("template_id")}>
              <SelectTrigger data-testid="form-template-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{templates.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Driver"><PlateCombobox value={f.driver_id} onChange={set("driver_id")} options={drivers.map((d) => ({ value: d.id, label: d.name }))} placeholder="Seleziona driver" searchPlaceholder="Cerca driver..." testId="form-driver-select" /></F>
          <F label="Nato/a a"><Input value={f.birth_place} onChange={set("birth_place")} data-testid="form-birth-place" /></F>
          <F label="Nato/a il"><Input type="date" value={f.birth_date} onChange={set("birth_date")} data-testid="form-birth-date" /></F>
          <F label="Luogo"><Input value={f.place} onChange={set("place")} placeholder="Es. Origgio" data-testid="form-place" /></F>
        </div>
        {t?.photos.map((label, i) => (
          <label key={label} className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 px-3 py-3 text-sm hover:border-slate-400" data-testid={`form-photo-${i}`}>
            <Camera className={`h-5 w-5 ${files[i] ? "text-emerald-600" : "text-slate-400"}`} />
            <span className="min-w-0 flex-1"><b>{label}</b><span className="block truncate text-xs text-slate-500">{files[i] ? files[i].name : "Tocca per fotografare o scegliere la foto"}</span></span>
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files[0] && setFiles({ ...files, [i]: e.target.files[0] })} data-testid={`form-photo-input-${i}`} />
          </label>
        ))}
        {t && <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">{t.body}</p>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => save(false)} disabled={busy || !f.template_id || !f.driver_id} data-testid="form-save-button">Salva (firma dopo)</Button>
          <Button onClick={() => save(true)} disabled={busy || !f.template_id || !f.driver_id} data-testid="form-save-sign-button">{busy ? "Salvataggio..." : "Salva e firma ora"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
